<template>
  <div class="inbound-list-component">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#9a6b27"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />

    <!-- Modern Notification Snackbar -->
    <v-snackbar v-model="showsnackbar" color="#9a6b27" right :timeout="timeout">
      <div class="d-flex align-center justify-space-between w-100">
        <span class="text-white font-weight-medium">{{ msg }}</span>
        <v-btn small icon text @click="showsnackbar = false">
          <v-icon color="#ffffff" size="18">mdi-close</v-icon>
        </v-btn>
      </div>
    </v-snackbar>

    <!-- Unified Stats & Filters Control Bar -->
    <div class="control-panel-card mb-3">
      <!-- Top Row: Compact KPI Stat Tiles -->
      <div class="stats-row d-flex flex-wrap align-center justify-space-between pb-3 mb-3 border-bottom gap-2">
        <div class="stats-strip d-flex flex-wrap align-center gap-2">
          <!-- Dispatched Net Weight -->
          <div class="stat-tile">
            <div class="stat-icon-wrap icon-before">
              <v-icon size="14" color="#d97706">mdi-package-up</v-icon>
            </div>
            <div>
              <div class="stat-label">DISPATCHED FOR ACIDIFYING</div>
              <div class="stat-val font-mono">
                {{ formatWeight(totals.totalBeforeAciding) }} <span class="stat-unit">gm</span>
              </div>
            </div>
          </div>

          <!-- Thankam Recovered -->
          <div class="stat-tile">
            <div class="stat-icon-wrap icon-thankam">
              <v-icon size="14" color="#059669">mdi-seal-variant</v-icon>
            </div>
            <div>
              <div class="stat-label">PURE THANKAM RECOVERED</div>
              <div class="stat-val font-mono text-thankam">
                {{ formatWeight(totals.totalThankamAfter) }} <span class="stat-unit">gm</span>
              </div>
            </div>
          </div>

          <!-- Yield Ratio -->
          <div class="stat-tile">
            <div class="stat-icon-wrap icon-ratio">
              <v-icon size="14" color="#7c3aed">mdi-chart-donut</v-icon>
            </div>
            <div>
              <div class="stat-label">RECOVERY YIELD</div>
              <div class="stat-val font-mono text-ratio">
                {{ recoveryYield }}
              </div>
            </div>
          </div>

          <!-- Self-Acidified Thankam Balance -->
          <div class="stat-tile">
            <div class="stat-icon-wrap icon-thankam">
              <v-icon size="14" color="#9a6b27">mdi-wallet-outline</v-icon>
            </div>
            <div>
              <div class="stat-label">JEWELLERY THANKAM BALANCE</div>
              <div class="stat-val font-mono text-thankam">
                {{ formatWeight(jewelleryThankamBalance) }} <span class="stat-unit">gm</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Refresh Button -->
        <div>
          <v-btn
            outlined
            depressed
            small
            class="btn-action-outline"
            @click="refresh()"
            title="Refresh Inbound Batches"
          >
            <v-icon left size="14">mdi-refresh</v-icon>
            Refresh
          </v-btn>
        </div>
      </div>

      <!-- Bottom Row: Filter Inputs -->
      <div class="d-flex flex-wrap align-center gap-2">
        <!-- From Date -->
        <div class="filter-box date-box">
          <v-menu
            ref="menuFrom"
            v-model="menuFrom"
            :close-on-content-click="false"
            min-width="auto"
            transition="scale-transition"
            offset-y
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="fromDate"
                placeholder="From Date"
                outlined
                dense
                readonly
                clearable
                hide-details
                color="#9a6b27"
                class="compact-input"
                prepend-inner-icon="mdi-calendar-start"
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="fromDate"
              no-title
              scrollable
              color="#9a6b27"
              @change="menuFrom = false"
            ></v-date-picker>
          </v-menu>
        </div>

        <!-- To Date -->
        <div class="filter-box date-box">
          <v-menu
            ref="menuTo"
            v-model="menuTo"
            :close-on-content-click="false"
            min-width="auto"
            transition="scale-transition"
            offset-y
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="toDate"
                placeholder="To Date"
                outlined
                dense
                readonly
                clearable
                hide-details
                color="#9a6b27"
                class="compact-input"
                prepend-inner-icon="mdi-calendar-end"
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="toDate"
              no-title
              scrollable
              color="#9a6b27"
              @change="menuTo = false"
            ></v-date-picker>
          </v-menu>
        </div>

        <!-- Status Filter -->
        <div class="filter-box status-box">
          <v-select
            :items="statusOptions"
            v-model="statusFilter"
            placeholder="All Statuses"
            item-text="text"
            item-value="value"
            outlined
            clearable
            hide-details
            dense
            color="#9a6b27"
            class="compact-input"
            prepend-inner-icon="mdi-filter-outline"
          ></v-select>
        </div>

        <!-- Search Batch No -->
        <div class="filter-box search-box">
          <v-text-field
            v-model="keyword"
            placeholder="Search Batch No..."
            outlined
            dense
            clearable
            hide-details
            color="#9a6b27"
            class="compact-input"
            prepend-inner-icon="mdi-magnify"
          ></v-text-field>
        </div>

        <!-- Clear Filters -->
        <v-btn
          v-if="fromDate || toDate || keyword || statusFilter !== null"
          text
          small
          class="btn-reset-filter"
          @click="clearFilters()"
        >
          <v-icon left size="13">mdi-filter-off-outline</v-icon>
          Reset
        </v-btn>
      </div>
    </div>

    <!-- Batches Data Table Card -->
    <div class="table-card rounded-lg">
      <div class="table-responsive">
        <table class="custom-table" v-if="List && List.length > 0">
          <thead>
            <tr>
              <th class="col-batch text-left">Batch No.</th>
              <th class="col-date text-left">Batch Date</th>
              <th class="col-wt text-right">Gross Wt (gm)</th>
              <th class="col-wt text-right">Net Wt (gm)</th>
              <th class="col-refiner text-left">Acidifier / Refiner</th>
              <th class="col-status text-center">Status</th>
              <th class="col-thankam text-right">Thankam (gm)</th>
              <th class="col-action text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(value, i) in List" :key="value._id || i">
              <!-- Batch No. -->
              <td class="text-left font-weight-bold">
                <span class="batch-badge">
                  {{ value.batchNumber || '-' }}
                </span>
              </td>

              <!-- Batch Date -->
              <td class="text-left text-date">
                {{ value.batchDate ? formatDate(value.batchDate) : '-' }}
              </td>

              <!-- Gross Wt -->
              <td class="text-right font-mono">
                {{ formatWeight(value.totalOldGoldGrossWeight) }}
              </td>

              <!-- Net Wt -->
              <td class="text-right font-mono font-weight-bold">
                {{ formatWeight(value.totalOldGoldNetWeight) }}
              </td>

              <!-- Acidifier -->
              <td class="text-left">
                <div class="d-flex align-center" v-if="value.acidifier">
                  <v-icon size="13" color="#64748b" class="mr-1">mdi-store-outline</v-icon>
                  <span class="refiner-name">{{ value.acidifier.supplierName }}</span>
                </div>
                <div class="d-flex align-center" v-else-if="value.acidifierType">
                  <v-icon size="13" color="#9a6b27" class="mr-1">mdi-home-analytics</v-icon>
                  <span class="refiner-self">{{ value.acidifierType }}</span>
                </div>
                <div class="d-flex align-center" v-else>
                  <v-icon size="13" color="#9a6b27" class="mr-1">mdi-home-analytics</v-icon>
                  <span class="refiner-self">Jewellery (Self)</span>
                </div>
              </td>

              <!-- Status Badge -->
              <td class="text-center">
                <span
                  class="status-badge"
                  :class="value.status === 'Batched' ? 'badge-pending' : 'badge-acidified'"
                >
                  <v-icon size="11" class="mr-1">
                    {{ value.status === 'Batched' ? 'mdi-clock-outline' : 'mdi-check-decagram' }}
                  </v-icon>
                  {{ value.status === 'Batched' ? 'PENDING' : 'ACIDIFIED' }}
                </span>
              </td>

              <!-- Thankam -->
              <td class="text-right font-mono font-weight-bold text-thankam">
                <span v-if="value.acidingOldGoldId && value.acidingOldGoldId.thankam !== undefined">
                  {{ formatWeight(value.acidingOldGoldId.thankam) }} gm
                </span>
                <span v-else class="text-muted font-weight-regular">
                  Pending
                </span>
              </td>

              <!-- Action Button -->
              <td class="text-center">
                <v-btn
                  v-if="value.status === 'Batched'"
                  depressed
                  small
                  class="btn-acidify-action"
                  @click="getAcidingDetails(value)"
                >
                  <v-icon left size="13">mdi-flask-outline</v-icon>
                  Acidify
                </v-btn>
                <v-btn
                  v-else
                  depressed
                  small
                  outlined
                  class="btn-action-outline"
                  @click="openViewDetails(value)"
                >
                  <v-icon left size="14">mdi-eye-outline</v-icon>
                  View
                </v-btn>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Empty State -->
        <div v-else class="empty-state-wrap pa-6 text-center">
          <v-icon size="40" color="#cbd5e1" class="mb-2">mdi-flask-empty-outline</v-icon>
          <div class="empty-title">No Batches Found</div>
          <div class="empty-desc text-muted mb-3">No refining batches match your filters.</div>
          <v-btn
            v-if="fromDate || toDate || keyword || statusFilter !== null"
            outlined
            small
            class="btn-action-outline"
            @click="clearFilters()"
          >
            Clear Filters
          </v-btn>
        </div>
      </div>

      <!-- Table Footer with Pagination -->
      <div class="table-footer-bar px-4 py-2 border-top d-flex flex-wrap align-center justify-space-between" v-if="List && List.length > 0">
        <div class="footer-info text-muted">
          Showing <strong>{{ List.length }}</strong> batches
        </div>
        <div class="footer-pagination">
          <v-pagination
            small
            v-model="page"
            :length="Pagelength || 1"
            color="#9a6b27"
            :total-visible="7"
          ></v-pagination>
        </div>
      </div>
    </div>

    <!-- Acidify Settlement Dialog Modal -->
    <v-dialog width="780px" v-model="addAciding" persistent>
      <v-form v-model="addcat" ref="addcat" @submit.prevent="validate()">
        <v-card class="acidify-modal rounded-xl">
          <!-- Modal Header -->
          <div class="modal-header d-flex align-center justify-space-between px-5 py-3 border-bottom">
            <div class="d-flex align-center">
              <div class="modal-icon-badge mr-3">
                <v-icon size="18" color="#9a6b27">mdi-flask-round-bottom-outline</v-icon>
              </div>
              <div>
                <h2 class="modal-title">Record Acidifying &amp; Settle Batch</h2>
                <p class="modal-subtitle mb-0">Log refining loss, purity touch, and receive pure Thankam</p>
              </div>
            </div>
            <v-btn icon small @click="closeAcidifyModal()">
              <v-icon color="#64748b" size="18">mdi-close</v-icon>
            </v-btn>
          </div>

          <!-- Dispatched Overview Banner -->
          <div class="modal-dispatched-banner px-5 py-2">
            <div class="d-flex flex-wrap align-center justify-space-between gap-2">
              <div class="summary-chip">
                <span class="chip-label">Batch Code:</span>
                <strong class="chip-val batch-badge">{{ batchNo }}</strong>
              </div>
              <div class="summary-chip">
                <span class="chip-label">Aciding Date:</span>
                <strong class="chip-val">{{ acidingDate || '-' }}</strong>
              </div>
              <div class="summary-chip">
                <span class="chip-label">Dispatched Net Gold:</span>
                <strong class="chip-val text-net font-weight-bold">{{ Number(beforeAciding || 0).toFixed(3) }} gm</strong>
              </div>
            </div>
          </div>

          <!-- Form Body -->
          <div class="modal-body px-5 py-3">
            <!-- Section 1: Deductions & Losses -->
            <div class="form-section-title mb-2">
              <v-icon size="14" color="#9a6b27" class="mr-1">mdi-scale-unbalanced</v-icon>
              Losses &amp; Deductions
            </div>
            <v-row dense class="mb-2">
              <!-- Melt Loss (gm) -->
              <v-col cols="12" sm="4">
                <label class="form-label">Melt Loss (gm) <span class="required">*</span></label>
                <v-text-field
                  v-model.number="acidingMeltWt"
                  type="number"
                  step="0.001"
                  outlined
                  dense
                  hide-details="auto"
                  color="#9a6b27"
                  class="compact-input font-amount"
                  placeholder="0.000"
                  @input="cal2()"
                ></v-text-field>
              </v-col>

              <!-- Dust Loss (gm) -->
              <v-col cols="12" sm="4">
                <label class="form-label">Dust Loss (gm) <span class="required">*</span></label>
                <v-text-field
                  v-model.number="acidingDust"
                  type="number"
                  step="0.001"
                  outlined
                  dense
                  hide-details="auto"
                  color="#9a6b27"
                  class="compact-input font-amount"
                  placeholder="0.000"
                  @input="cal2()"
                ></v-text-field>
              </v-col>

              <!-- Test Piece (gm) -->
              <v-col cols="12" sm="4">
                <label class="form-label">Test Piece (gm) <span class="required">*</span></label>
                <v-text-field
                  v-model.number="testPiece"
                  type="number"
                  step="0.001"
                  outlined
                  dense
                  hide-details="auto"
                  color="#9a6b27"
                  class="compact-input font-amount"
                  placeholder="0.000"
                  @input="cal2()"
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- Live Calculation Outcome Card -->
            <div class="thankam-calculation-card mb-3 pa-3 rounded-lg">
              <div class="d-flex flex-wrap align-center justify-space-between gap-2">
                <div>
                  <div class="calc-label">RETURNED THANKAM (PURE GOLD)</div>
                  <div class="calc-formula text-muted">
                    {{ Number(beforeAciding || 0).toFixed(3) }} gm (Dispatched) - {{ Number(acidingMeltWt || 0).toFixed(3) }} gm (Melt) - {{ Number(acidingDust || 0).toFixed(3) }} gm (Dust) - {{ Number(testPiece || 0).toFixed(3) }} gm (Test)
                  </div>
                </div>
                <div class="calc-result text-right">
                  <span class="calc-value font-mono">{{ Number(afterAcidingWt || 0).toFixed(3) }}</span>
                  <span class="calc-unit">gm</span>
                </div>
              </div>
            </div>

            <!-- Section 2: Purity, Rate & Return Date -->
            <div class="form-section-title mb-2">
              <v-icon size="14" color="#9a6b27" class="mr-1">mdi-seal</v-icon>
              Purity &amp; Settlement Details
            </div>
            <v-row dense>
              <!-- Touch (%) -->
              <v-col cols="12" sm="3">
                <label class="form-label">Touch (%) <span class="required">*</span></label>
                <v-text-field
                  v-model.number="acidingTouchPercent"
                  type="number"
                  step="0.01"
                  outlined
                  dense
                  hide-details="auto"
                  color="#9a6b27"
                  class="compact-input font-amount"
                  placeholder="e.g. 99.5"
                ></v-text-field>
              </v-col>

              <!-- Touch of test piece (%) -->
              <v-col cols="12" sm="3">
                <label class="form-label">Test Piece Touch (%) <span class="required">*</span></label>
                <v-text-field
                  v-model.number="touchoftestpiece"
                  type="number"
                  step="0.01"
                  outlined
                  dense
                  hide-details="auto"
                  color="#9a6b27"
                  class="compact-input font-amount"
                  placeholder="e.g. 91.6"
                ></v-text-field>
              </v-col>

              <!-- Boarding Rate (₹) -->
              <v-col cols="12" sm="3">
                <label class="form-label">Boarding Rate (₹) <span class="required">*</span></label>
                <v-text-field
                  v-model.number="acidingBoardingRate"
                  type="number"
                  outlined
                  dense
                  hide-details="auto"
                  color="#9a6b27"
                  class="compact-input font-amount"
                  placeholder="e.g. 7200"
                ></v-text-field>
              </v-col>

              <!-- Return Date -->
              <v-col cols="12" sm="3">
                <label class="form-label">Return Date <span class="required">*</span></label>
                <v-menu
                  ref="menu2"
                  v-model="menu2"
                  :close-on-content-click="false"
                  min-width="auto"
                  transition="scale-transition"
                  offset-y
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="returnDate"
                      outlined
                      dense
                      readonly
                      hide-details="auto"
                      color="#9a6b27"
                      class="compact-input"
                      append-icon="mdi-calendar"
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="returnDate"
                    color="#9a6b27"
                    @change="menu2 = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>
          </div>

          <!-- Modal Footer Actions -->
          <div class="modal-footer px-5 py-3 border-top d-flex align-center justify-end gap-2">
            <v-btn
              outlined
              small
              class="btn-action-outline mr-1"
              @click="closeAcidifyModal()"
              :disabled="submitting"
            >
              Cancel
            </v-btn>
            <v-btn
              depressed
              small
              class="btn-primary-gold"
              :loading="submitting"
              @click="validate()"
            >
              <v-icon left size="14">mdi-check-all</v-icon>
              Confirm &amp; Settle Batch
            </v-btn>
          </div>
        </v-card>
      </v-form>
    </v-dialog>

    <!-- Detailed Report View Dialog -->
    <v-dialog width="700px" v-model="viewDialog">
      <v-card class="acidify-modal rounded-xl" v-if="viewItem">
        <!-- Modal Header -->
        <div class="modal-header d-flex align-center justify-space-between px-5 py-3 border-bottom">
          <div class="d-flex align-center">
            <div class="modal-icon-badge mr-3">
              <v-icon size="18" color="#9a6b27">mdi-file-document-outline</v-icon>
            </div>
            <div>
              <h2 class="modal-title">Acidifying Batch Report</h2>
              <p class="modal-subtitle mb-0">Full breakdown of refining loss, purity and Thankam recovery</p>
            </div>
          </div>
          <v-btn icon small @click="closeViewDetails()">
            <v-icon color="#64748b" size="18">mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="modal-dispatched-banner px-5 py-2">
          <div class="d-flex flex-wrap align-center justify-space-between gap-2">
            <div class="summary-chip">
              <span class="chip-label">Batch Code:</span>
              <strong class="chip-val batch-badge">{{ viewItem.batchNumber || '-' }}</strong>
            </div>
            <div class="summary-chip">
              <span class="chip-label">Batch Date:</span>
              <strong class="chip-val">{{ viewItem.batchDate ? formatDate(viewItem.batchDate) : '-' }}</strong>
            </div>
            <div class="summary-chip">
              <span class="chip-label">Acidifier:</span>
              <strong class="chip-val">
                {{ viewItem.acidifier ? viewItem.acidifier.supplierName : (viewItem.acidifierType || 'Jewellery (Self)') }}
              </strong>
            </div>
          </div>
        </div>

        <div class="modal-body px-5 py-4">
          <div class="form-section-title mb-3">
            <span class="section-icon-pill mr-2">
              <v-icon size="13" color="#9a6b27">mdi-scale-unbalanced</v-icon>
            </span>
            Dispatched &amp; Losses
          </div>
          <div class="stat-tile-grid mb-4">
            <div class="stat-tile">
              <div class="report-stat-label">Gross Wt (gm)</div>
              <div class="report-stat-val">{{ formatWeight(viewItem.totalOldGoldGrossWeight) }}</div>
            </div>
            <div class="stat-tile">
              <div class="report-stat-label">Net Wt (gm)</div>
              <div class="report-stat-val">{{ formatWeight(viewItem.totalOldGoldNetWeight) }}</div>
            </div>
            <div class="stat-tile stat-tile-loss">
              <div class="report-stat-label">Melt Loss (gm)</div>
              <div class="report-stat-val">{{ formatWeight(acidingDetail.meltingLoss) }}</div>
            </div>
            <div class="stat-tile stat-tile-loss">
              <div class="report-stat-label">Dust Loss (gm)</div>
              <div class="report-stat-val">{{ formatWeight(acidingDetail.dust) }}</div>
            </div>
            <div class="stat-tile">
              <div class="report-stat-label">Test Piece (gm)</div>
              <div class="report-stat-val">{{ formatWeight(acidingDetail.testPiece) }}</div>
            </div>
            <div class="stat-tile">
              <div class="report-stat-label">Net for Acidification (gm)</div>
              <div class="report-stat-val">{{ formatWeight(acidingDetail.netWeightForAcidification) }}</div>
            </div>
          </div>

          <div class="form-section-title mb-3">
            <span class="section-icon-pill mr-2">
              <v-icon size="13" color="#9a6b27">mdi-seal</v-icon>
            </span>
            Purity &amp; Settlement
          </div>
          <div class="stat-tile-grid mb-4">
            <div class="stat-tile">
              <div class="report-stat-label">Received Gross Wt (gm)</div>
              <div class="report-stat-val">{{ formatWeight(acidingDetail.receivedGrossWeight) }}</div>
            </div>
            <div class="stat-tile">
              <div class="report-stat-label">Received Net Wt (gm)</div>
              <div class="report-stat-val">{{ formatWeight(acidingDetail.receivedNetWeight) }}</div>
            </div>
            <div class="stat-tile stat-tile-accent">
              <div class="report-stat-label">Touch (%)</div>
              <div class="report-stat-val">{{ acidingDetail.touch != null ? acidingDetail.touch : '-' }}</div>
            </div>
            <div class="stat-tile stat-tile-accent">
              <div class="report-stat-label">Test Piece Touch (%)</div>
              <div class="report-stat-val">{{ acidingDetail.touchOfTestPiece != null ? acidingDetail.touchOfTestPiece : '-' }}</div>
            </div>
            <div class="stat-tile">
              <div class="report-stat-label">Thankam Rate (₹)</div>
              <div class="report-stat-val">{{ acidingDetail.thankamRate != null ? acidingDetail.thankamRate : '-' }}</div>
            </div>
            <div class="stat-tile">
              <div class="report-stat-label">Return Date</div>
              <div class="report-stat-val report-stat-val-sm">{{ acidingDetail.thankamReturnDate ? formatDate(acidingDetail.thankamReturnDate) : '-' }}</div>
            </div>
          </div>

          <div class="thankam-calculation-card pa-4 rounded-lg d-flex align-center">
            <div class="calc-icon-pill mr-3">
              <v-icon size="20" color="#15803d">mdi-gold</v-icon>
            </div>
            <div class="d-flex flex-wrap align-center justify-space-between flex-grow-1 gap-2">
              <div class="calc-label">Pure Thankam Recovered</div>
              <div class="calc-result text-right">
                <span class="calc-value font-mono">{{ formatWeight(acidingDetail.thankam) }}</span>
                <span class="calc-unit">gm</span>
              </div>
            </div>
          </div>

          <div v-if="acidingDetail.remarks" class="remarks-box mt-3 pa-3 rounded-lg">
            <div class="report-stat-label">Remarks</div>
            <div class="report-stat-val font-weight-regular">{{ acidingDetail.remarks }}</div>
          </div>
        </div>

        <div class="modal-footer px-5 py-3 border-top d-flex align-center justify-end gap-2">
          <v-btn
            outlined
            small
            class="btn-action-outline"
            @click="closeViewDetails()"
          >
            Close
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "InboundList",
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 2500,
      msg: null,
      fromDate: null,
      toDate: null,
      keyword: null,
      menuFrom: false,
      menuTo: false,
      statusFilter: null,
      statusOptions: [
        { text: "All Statuses", value: null },
        { text: "Acidified & Settled", value: "Acidified" },
      ],
      page: 1,
      Pagelength: 1,
      List: [],
      totals: { totalBeforeAciding: 0, totalThankamAfter: 0 },
      jewelleryThankamBalance: 0,
      viewDialog: false,
      viewItem: null,
      addcat: false,
      menu2: false,
      addAciding: false,
      submitting: false,
      batchNo: "",
      batchCode: "",
      beforeAciding: "",
      acidingDate: "",
      testPiece: "",
      acidingTouchPercent: "",
      afterAcidingWt: "",
      acidingBoardingRate: "",
      returnDate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substr(0, 10),
      acidingMeltWt: "",
      acidingDust: "",
      touchoftestpiece: "",
    };
  },
  computed: {
    recoveryYield() {
      const before = Number(this.totals.totalBeforeAciding) || 0;
      const after = Number(this.totals.totalThankamAfter) || 0;
      if (!before || before <= 0) return "N/A";
      const pct = (after / before) * 100;
      return `${pct.toFixed(2)}%`;
    },
    acidingDetail() {
      return (this.viewItem && this.viewItem.acidingOldGoldId) || {};
    },
  },
  watch: {
    fromDate() {
      this.page = 1;
      this.getList();
    },
    toDate() {
      this.page = 1;
      this.getList();
    },
    keyword() {
      this.page = 1;
      this.getList();
    },
    statusFilter() {
      this.page = 1;
      this.getList();
    },
    page() {
      this.getList();
    },
  },
  mounted() {
    this.getList();
    this.getThankamBalance();
  },
  methods: {
    refresh() {
      this.getList();
      this.getThankamBalance();
    },
    getThankamBalance() {
      axios({
        url: "/oldgold/acidifiedthankam/balance",
        method: "get",
        headers: { "x-auth-token": localStorage.getItem("token") },
      })
        .then((response) => {
          if (response.data.status === true) {
            this.jewelleryThankamBalance = response.data.acidifiedThankam || 0;
          }
        })
        .catch((err) => {
          console.error("Error fetching thankam balance:", err);
        });
    },
    getList() {
      this.appLoading = true;
      axios({
        url: "/oldgoldbatchlist/basedonstatus",
        method: "get",
        headers: { "x-auth-token": localStorage.getItem("token") },
        params: {
          count: 20,
          page: this.page,
          from: this.fromDate,
          to: this.toDate,
          keyword: this.keyword,
          status: this.statusFilter,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.List = response.data.data || [];
            this.totals = response.data.totals || this.totals;
            const totalRecords = response.data.count || 0;
            this.Pagelength = Math.max(1, Math.ceil(totalRecords / 20));
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error("Error fetching inbound batches:", err);
        });
    },
    getAcidingDetails(items) {
      this.batchCode = items._id;
      this.batchNo = items.batchNumber;
      this.addAciding = true;
      this.checktype(items._id);
    },
    openViewDetails(items) {
      this.viewItem = items;
      this.viewDialog = true;
    },
    closeViewDetails() {
      this.viewDialog = false;
      this.viewItem = null;
    },
    cal2() {
      let base = Number(this.beforeAciding) || 0;
      if (this.acidingMeltWt) {
        base -= Number(this.acidingMeltWt);
      }
      if (this.testPiece) {
        base -= Number(this.testPiece);
      }
      if (this.acidingDust) {
        base -= Number(this.acidingDust);
      }
      this.afterAcidingWt = Math.max(0, Number(base.toFixed(3)));
    },
    checktype(item) {
      const singleobj = this.List.find((x) => x._id === item);
      if (singleobj) {
        this.beforeAciding = singleobj.totalOldGoldNetWeight || 0;
        this.acidingDate = this.formatDate(singleobj.batchDate);
        this.cal2();
      }
    },
    closeAcidifyModal() {
      this.addAciding = false;
      this.resetForm();
    },
    resetForm() {
      this.batchNo = "";
      this.batchCode = "";
      this.beforeAciding = "";
      this.acidingDate = "";
      this.testPiece = "";
      this.acidingTouchPercent = "";
      this.afterAcidingWt = "";
      this.acidingBoardingRate = "";
      this.returnDate = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substr(0, 10);
      this.acidingMeltWt = "";
      this.acidingDust = "";
      this.touchoftestpiece = "";
    },
    validate() {
      if (!this.batchCode) {
        this.msg = "Please select a valid batch code";
        this.showsnackbar = true;
      } else if (!this.acidingBoardingRate) {
        this.msg = "Boarding rate cannot be empty";
        this.showsnackbar = true;
      } else if (!this.beforeAciding) {
        this.msg = "Before aciding weight cannot be empty";
        this.showsnackbar = true;
      } else if (!this.returnDate) {
        this.msg = "Please choose return date";
        this.showsnackbar = true;
      } else if (!this.acidingDate) {
        this.msg = "Aciding date cannot be empty";
        this.showsnackbar = true;
      } else if (this.acidingMeltWt === "" || this.acidingMeltWt === null) {
        this.msg = "Please enter melt weight (0 if none)";
        this.showsnackbar = true;
      } else if (this.testPiece === "" || this.testPiece === null) {
        this.msg = "Please enter test piece weight";
        this.showsnackbar = true;
      } else if (!this.acidingTouchPercent) {
        this.msg = "Please enter touch percentage";
        this.showsnackbar = true;
      } else if (!this.touchoftestpiece) {
        this.msg = "Please enter touch of test piece";
        this.showsnackbar = true;
      } else if (this.afterAcidingWt === "" || this.afterAcidingWt === null) {
        this.msg = "After aciding weight cannot be empty";
        this.showsnackbar = true;
      } else if (Number(this.afterAcidingWt) > Number(this.beforeAciding)) {
        this.msg = "Thankam weight cannot be greater than dispatched gold";
        this.showsnackbar = true;
      } else {
        this.addBatch();
      }
    },
    addBatch() {
      this.submitting = true;
      axios({
        url: "/acidingdata/add",
        method: "POST",
        data: {
          batchNumber: this.batchCode,
          beforeacidingWeight: this.beforeAciding,
          acidingDate: this.acidingDate,
          thankamReturnDate: this.returnDate,
          testPiece: this.testPiece,
          touch: this.acidingTouchPercent,
          thankam: this.afterAcidingWt,
          thankamRate: this.acidingBoardingRate,
          meltingLoss: this.acidingMeltWt,
          dust: this.acidingDust,
          touchOfTestPiece: this.touchoftestpiece,
        },
        headers: { "x-auth-token": localStorage.getItem("token") },
      })
        .then((response) => {
          this.submitting = false;
          if (response.data.status === true) {
            this.msg = response.data.msg || "Batch settled and pure gold received!";
            this.showsnackbar = true;
            this.addAciding = false;
            this.resetForm();
            this.getList();
            this.getThankamBalance();
          } else {
            this.msg = response.data.msg || "Failed to settle batch";
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.submitting = false;
          this.ServerError = true;
          console.error("Error settling batch:", err);
        });
    },
    clearFilters() {
      this.fromDate = null;
      this.toDate = null;
      this.keyword = null;
      this.statusFilter = null;
      this.page = 1;
      this.getList();
    },
    formatDate(item) {
      if (!item) return "-";
      const dt = new Date(item);
      if (isNaN(dt.getTime())) return item;
      const day = dt.getDate();
      const monthNames = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
      ];
      return `${day} ${monthNames[dt.getMonth()]} ${dt.getFullYear()}`;
    },
    formatWeight(val) {
      if (val === undefined || val === null || isNaN(val)) return "0.000";
      return Number(val).toFixed(3);
    },
  },
};
</script>

<style scoped>
.inbound-list-component {
  font-family: "Plus Jakarta Sans", "Inter", sans-serif;
  color: #0f172a;
}

/* Utilities */
.gap-2 { gap: 8px; }
.w-100 { width: 100%; }

/* Unified Control Panel Card */
.control-panel-card {
  background: #ffffff;
  border: 1px solid #e8e4dc;
  border-radius: 12px;
  padding: 12px 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.border-bottom {
  border-bottom: 1px solid #f1f5f9;
}

/* Stat Tiles */
.stat-tile {
  display: inline-flex;
  align-items: center;
  background: #fdfcfb;
  border: 1px solid #eee9e0;
  border-radius: 8px;
  padding: 6px 12px;
}

.stat-icon-wrap {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
}

.icon-before { background: #fffbeb; }
.icon-thankam { background: #ecfdf5; }
.icon-ratio { background: #f5f3ff; }

.stat-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: #64748b;
  text-transform: uppercase;
  line-height: 1.1;
}

.stat-val {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
}

.stat-unit {
  font-size: 11px;
  font-weight: 500;
  color: #64748b;
}

.text-thankam { color: #047857; }
.text-ratio { color: #7c3aed; }

/* Filter Inputs */
.date-box {
  width: 140px;
}

.status-box {
  width: 170px;
}

.search-box {
  width: 180px;
}

.compact-input >>> .v-input__slot {
  min-height: 34px !important;
  height: 34px !important;
  font-size: 12.5px !important;
  border-radius: 7px !important;
  background-color: #f8fafc !important;
  border-color: #e2e8f0 !important;
  padding: 0 8px !important;
}

.compact-input >>> fieldset {
  border-color: #e2e8f0 !important;
}

.compact-input:hover >>> fieldset {
  border-color: #cbd5e1 !important;
}

.compact-input.v-input--is-focused >>> fieldset {
  border-color: #9a6b27 !important;
}

.compact-input >>> .v-input__prepend-inner {
  margin-top: 6px !important;
  margin-right: 4px !important;
}

.compact-input >>> .v-icon {
  font-size: 15px !important;
  color: #94a3b8 !important;
}

.compact-input >>> input {
  font-family: "Plus Jakarta Sans", sans-serif !important;
  font-size: 12.5px !important;
}

.font-amount >>> input {
  font-weight: 700 !important;
}

.btn-reset-filter {
  color: #64748b !important;
  text-transform: none !important;
  font-size: 11.5px !important;
}

/* Table Card */
.table-card {
  background-color: #ffffff;
  border: 1px solid #e8e4dc;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  overflow: hidden;
}

.border-top { border-top: 1px solid #f1f5f9; }

.table-responsive {
  width: 100%;
  overflow-x: auto;
}

.custom-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}

.custom-table thead {
  background-color: #fafaf9;
}

.custom-table th {
  padding: 10px 14px;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  white-space: nowrap;
  border-bottom: 1px solid #eef1f5;
  user-select: none;
}

.custom-table td {
  padding: 10px 14px;
  font-size: 13px;
  color: #334155;
  border-bottom: 1px solid #f8fafc;
  vertical-align: middle;
}

.custom-table tbody tr:hover td {
  background-color: #faf8f5;
}

.batch-badge {
  display: inline-block;
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 12px;
  font-weight: 700;
  color: #0f172a;
  background-color: #f1f5f9;
  border: 1px solid #e2e8f0;
  padding: 2px 7px;
  border-radius: 5px;
}

.text-date {
  color: #475569;
  font-size: 12.5px;
  white-space: nowrap;
}

.font-mono {
  font-variant-numeric: tabular-nums;
}

.refiner-name {
  font-weight: 600;
  color: #0f172a;
  font-size: 12.5px;
}

.refiner-self {
  font-weight: 600;
  color: #9a6b27;
  font-size: 12.5px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 9999px;
  letter-spacing: 0.02em;
}

.badge-pending {
  background-color: #fffbeb;
  color: #b45309;
  border: 1px solid #fde68a;
}

.badge-acidified {
  background-color: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
}

.btn-acidify-action {
  height: 28px !important;
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important;
  color: #ffffff !important;
  font-size: 11px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 5px !important;
  padding: 0 10px !important;
  box-shadow: 0 2px 4px rgba(154, 106, 44, 0.2) !important;
}

.action-completed {
  display: inline-flex;
  align-items: center;
  font-size: 11.5px;
  font-weight: 600;
  color: #059669;
}

.empty-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

.empty-desc {
  font-size: 12px;
}

.footer-info {
  font-size: 12px;
}

/* Modal */
.acidify-modal {
  background: #ffffff;
  overflow: hidden;
}

.modal-icon-badge {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #fbf5ec;
  border: 1px solid #ebd9be;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.modal-subtitle {
  font-size: 12px;
  color: #64748b;
}

.modal-dispatched-banner {
  background: #fafaf9;
  border-bottom: 1px solid #f1f5f9;
}

.summary-chip {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  color: #475569;
}

.chip-label { margin-right: 5px; }

.form-section-title {
  font-size: 11px;
  font-weight: 700;
  color: #475569;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  display: flex;
  align-items: center;
}

.section-icon-pill {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: #fbf5ec;
  border: 1px solid #ebd9be;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-tile-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

@media (max-width: 600px) {
  .stat-tile-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.stat-tile-grid .stat-tile {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  background: #fafafa;
  border: 1px solid #eef0f2;
  border-radius: 8px;
  padding: 9px 12px;
  min-width: 0;
}

.stat-tile-loss .report-stat-val {
  color: #b45309;
}

.stat-tile-accent .report-stat-val {
  color: #9a6b27;
}

.report-stat-val-sm {
  font-size: 12.5px;
}

.remarks-box {
  background: #fafaf9;
  border: 1px solid #f1f5f9;
}

.form-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  color: #475569;
  letter-spacing: 0.03em;
  margin-bottom: 4px;
  text-transform: uppercase;
}

.required { color: #ef4444; }

.thankam-calculation-card {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
}

.calc-icon-pill {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #dcfce7;
  border: 1px solid #bbf7d0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.calc-label {
  font-size: 11.5px;
  font-weight: 700;
  color: #15803d;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.calc-formula {
  font-size: 11px;
  margin-top: 2px;
}

.calc-value {
  font-size: 22px;
  font-weight: 800;
  color: #15803d;
}

.calc-unit {
  font-size: 13px;
  font-weight: 600;
  color: #16a34a;
  margin-left: 6px;
}

.btn-primary-gold {
  height: 34px !important;
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important;
  color: #ffffff !important;
  font-family: "Plus Jakarta Sans", sans-serif !important;
  font-size: 12.5px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 7px !important;
  padding: 0 14px !important;
}

.btn-action-outline {
  height: 34px !important;
  border: 1px solid #e2e8f0 !important;
  color: #475569 !important;
  font-size: 12.5px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 7px !important;
  padding: 0 12px !important;
}

.btn-action-outline:hover {
  border-color: #9a6b27 !important;
  color: #9a6b27 !important;
}

.report-stat-label {
  font-size: 10px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  margin-bottom: 2px;
}

.report-stat-val {
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
  font-family: "Roboto Mono", monospace;
}
</style>
